/**
 * Work through one group of the task queue (`/tasks?work=<group key>`): one
 * task at a time in work order – open the chat (copying the draft), send,
 * Sent ✓ – and the next task appears. Skip leaves a task open for later,
 * Decline needs a reason, every close has Undo. Keys: Enter = the primary
 * action, d = Done / Sent ✓, s = Skip, Esc = leave (all stand down in text
 * fields and while an overlay is open).
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import type { HumanTask } from "../../api/tasks";
import { useKeyboardShortcuts } from "../../lib/hooks/useKeyboardShortcuts";
import { useTaskActions } from "../../lib/hooks/useTaskActions";
import { openPrimary } from "../../lib/taskActions";
import { groupCampaignHref, groupProgressText, type TaskGroup } from "../../lib/taskGroups";
import {
  WORK_START,
  currentTask,
  handleTask,
  primaryAction,
  skipTask,
  undoTask,
  workProgress,
  type WorkState,
} from "../../lib/workThrough";
import { useToast } from "../toast/context";
import { TaskCard } from "./TaskCard";

export function WorkThrough({
  group,
  label,
  nowMs,
  onExit,
}: {
  /** The group's current open tasks; null once none is left. */
  group: TaskGroup | null;
  /** The group label – kept from the start, the group may vanish when done. */
  label: string;
  nowMs: number;
  onExit: () => void;
}) {
  const [state, setState] = useState<WorkState>(WORK_START);
  const actions = useTaskActions();
  const toast = useToast();
  const tasks = group?.tasks ?? [];
  const current = currentTask(state, tasks);
  const progress = workProgress(state, tasks);
  const busy = current ? actions.busyId === current.task_id : false;

  const onUndo = (task: HumanTask) => () => setState((s) => undoTask(s, task.task_id));

  async function close(task: HumanTask) {
    if (await actions.complete(task, { onUndo: onUndo(task) })) {
      setState((s) => handleTask(s, tasks, task.task_id));
    }
  }

  async function decline(task: HumanTask, reason: string): Promise<boolean> {
    const ok = await actions.decline(task, reason, { onUndo: onUndo(task) });
    if (ok) setState((s) => handleTask(s, tasks, task.task_id));
    return ok;
  }

  async function primary(task: HumanTask) {
    if (primaryAction(task).kind === "done") {
      await close(task);
      return;
    }
    const result = await openPrimary(task);
    if (result.error) toast.error(result.error);
  }

  useKeyboardShortcuts({
    Enter: () => {
      if (current && !busy) void primary(current);
    },
    d: () => {
      if (current && !busy) void close(current);
    },
    s: () => {
      if (current && !busy) setState((s) => skipTask(s, tasks));
    },
    Escape: onExit,
  });

  const campaignHref = group ? groupCampaignHref(group) : null;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="min-w-0">
          <div className="text-xs text-ink-low">Work through</div>
          <div className="font-heading font-semibold text-ink">{label}</div>
        </div>
        {campaignHref ? (
          <Link to={campaignHref} className="link text-sm">
            Campaign →
          </Link>
        ) : null}
        <span className="ml-auto text-sm text-ink-low">
          {current ? (
            <>
              Task <span className="tnum text-ink">{progress.position}</span> of{" "}
              <span className="tnum">{progress.total}</span>
            </>
          ) : null}
          {group?.progress ? <span className="ml-3">{groupProgressText(group)}</span> : null}
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onExit}>
          Leave · Esc
        </button>
      </div>

      {current ? (
        <TaskCard
          key={current.task_id}
          task={current}
          nowMs={nowMs}
          busy={busy}
          emphasis
          onPrimary={() => void primary(current)}
          onClose={() => void close(current)}
          onSkip={() => setState((s) => skipTask(s, tasks))}
          onDecline={(reason) => decline(current, reason)}
        />
      ) : (
        <div className="panel text-sm">
          <div className="font-semibold text-ink">
            {state.handled.length
              ? `Done: ${state.handled.length} task${state.handled.length === 1 ? "" : "s"} handled.`
              : "No open task in this group."}
          </div>
          {state.skipped.length ? (
            <div className="text-ink-low mt-1">
              {state.skipped.length} skipped task{state.skipped.length === 1 ? " stays" : "s stay"} open.{" "}
              <button
                type="button"
                className="link"
                onClick={() => setState((s) => ({ ...s, skipped: [], currentId: null }))}
              >
                Go through them again
              </button>
            </div>
          ) : null}
          <button type="button" className="btn btn-main btn-sm mt-3" onClick={onExit}>
            Back to the queue
          </button>
        </div>
      )}
      <div className="text-xs text-ink-low mt-3">
        Enter = {current ? primaryAction(current).label : "primary action"} · d = done · s = skip · Esc = leave
      </div>
    </div>
  );
}
